/* eslint-disable max-lines-per-function */
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Grid } from "@mui/material";
import { useQuery } from "@apollo/client";
import { gql } from "graphql-tag";
import { Form, Formik, FormikConfig } from "formik";
import * as yup from "yup";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { Card, Button } from "components/haven-design";
import { usePostgresViewer } from "components/viewer/context";
import { FetchUserExistance, FetchUserExistanceVariables, getViewer } from "types/gql-types";
import { VIEWER_QUERY } from "components/auth/query";
import { LoadingSpinner } from "components/graphql-views";
import ErrorMessage from "components/error-message";
import { PasswordValidationType, validatePassword } from "components/login/utils";
import { PasswordValidations, BackToLogin, HavenConnectLoginLogo, LoginCardSubtitle, LoginCardTitle, LoneGrid } from "components/login/components/shared-components";
import loginCss from "components/login/components/Login.module.css";
import { loginAuthColumnSx } from "components/login/components/styles";
import { useSetNewUserPassword } from "./actions";
import { loginStyles } from "./styles";

const FETCH_USER_EXISTANCE = gql`
  query FetchUserExistance($token: String!) {
    userExists(token: $token)
  }
`;

export const NewUser: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<PasswordValidationType[]>([]);
  const setNewUserPassword = useSetNewUserPassword();

  const viewer = usePostgresViewer();
  const { loading, error, data } = useQuery<getViewer>(VIEWER_QUERY);

  const { loading: loadingExists, error: errorExists, data: dataExists } =
    useQuery<FetchUserExistance, FetchUserExistanceVariables>(FETCH_USER_EXISTANCE, {
      variables: { token: token || "" },
    });


  useEffect(() => {
    if (viewer && data && data.viewer) {
      navigate(data.viewer.isPropManager || data.viewer.isSupervisor ? "/new-user/update" : "/");
    }
  }, [viewer, data]);

  useEffect(() => {
    setErrors(validatePassword(password));
  }, [password]);

  const titleText = dataExists && !dataExists.userExists ? "Link Expired" : "Welcome to Haven Connect!";
  const subtitleText = "First, let's set your password. Your username is your work email address.";

  const onSubmit: FormikConfig<{ password: string }>["onSubmit"] = async (values, { setSubmitting }) => {
    try {
      await setNewUserPassword(token || "", values.password);
      navigate("/new-user/update");
    } catch {
      setSubmitting(false);
    }
  };

  return (
    <Grid container justifyContent="center" spacing={2} direction="row">
      <Grid size={{ xs: 12 }}>
        <HavenConnectLoginLogo />
      </Grid>
      <Card>
        <LoneGrid><LoginCardTitle title={titleText} /></LoneGrid>

        {(loadingExists || loading) && <LoadingSpinner/>}
        {(errorExists || error) && <ErrorMessage errorMsg="Failed to verify the token"/>}
        {dataExists && (
          dataExists.userExists ? (
            <>
              <LoneGrid><LoginCardSubtitle>{subtitleText}</LoginCardSubtitle></LoneGrid>
              <Formik
                initialValues={{ password }}
                onSubmit={onSubmit}
                validationSchema={yup.object().shape({
                  password: yup.string().required().validPassword(),
                })}
                enableReinitialize={true}
              >
                {({ isSubmitting: formSubmitting }) => (
                  <Form style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
                    <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
                      <InputWithErrorLabel
                        name="password"
                        label="New Password"
                        type="password"
                        style={loginStyles.loginInput}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                      />
                      <PasswordValidations errors={errors} />
                    </Grid>
                    <Grid size={{ xs: 12 }}><br/></Grid>
                    <LoneGrid>
                      <Grid size={{ xs: 12 }} sx={loginAuthColumnSx}>
                        <Button
                          disabled={errors.length > 0 || formSubmitting}
                          size="L"
                          color="primary"
                          type="submit"
                          fullWidth
                          className={loginCss.loginButton}
                          style={loginStyles.btn}
                        >
                          Save and Continue
                        </Button>
                      </Grid>
                    </LoneGrid>
                    <Grid size={{ md: 12 }}><BackToLogin/></Grid>
                  </Form>
                )}
              </Formik>
            </>
          ) : (
            <LoneGrid>
              <LoginCardSubtitle>
                This link has expired.
                <br/>
                Please try to <Link to="/login">log in</Link> or <Link to="/reset-password">reset your password</Link>.
              </LoginCardSubtitle>
            </LoneGrid>
          ))}
      </Card>
    </Grid>
  );
};
